/**
 * Hook to check for pending A/B test sessions
 * Used to restore A/B test modal after page refresh
 */

import { useState, useEffect } from 'react';
import { fetchWithAuth } from '@/lib/api';

interface ABTestSession {
  id: string;
  optionA: any;
  optionB: any;
  status: string;
  createdAt: string;
}

export function useIngestionABTestSession(todoId: string | null) {
  const [session, setSession] = useState<ABTestSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!todoId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    fetchWithAuth(`/api/ai/ingestion/ab-test-session/${todoId}`)
      .then(async (res) => {
        const contentType = res.headers.get('content-type') ?? '';
        if (!res.ok || !contentType.includes('application/json')) {
          return null;
        }
        try {
          return await res.json();
        } catch {
          return null;
        }
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.session) {
          setSession(data.session);
        } else {
          setSession(null);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!(err instanceof SyntaxError)) {
          console.warn('[AB Test Session] load failed:', err);
          setError(err instanceof Error ? err.message : 'Failed to load session');
        }
        setSession(null);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [todoId]);

  return { session, isLoading, error };
}

